import { getApi, postApi } from 'apis';
import type { DraftEditPrefetchedReferenceData, MedicineDetailsByIdsRow } from './types';

function assertOk(res: any, fallbackMessage: string) {
	if (res?.data?.responseStatus !== 200) {
		throw new Error(res?.data?.message || fallbackMessage);
	}
}

export async function fetchDraftMedicineEditBundle(
	medicineId: string
): Promise<{ medicine: MedicineDetailsByIdsRow } & DraftEditPrefetchedReferenceData> {
	const [detailsRes, warningRes, categoryRes, doseRes] = await Promise.all([
		postApi('/medicine/details-by-ids', { medicineIds: [medicineId] }, false),
		getApi('/warning-tags/get-all'),
		getApi('/category/get-all', { limit: 1000 }),
		getApi('/dose-form/get-all'),
	]);

	assertOk(detailsRes, 'Failed to load medicine details');
	assertOk(warningRes, 'Failed to load warning tags');
	assertOk(categoryRes, 'Failed to fetch categories');
	assertOk(doseRes, 'Failed to fetch dose forms');

	const medicine = detailsRes?.data?.data?.[0] as MedicineDetailsByIdsRow | undefined;
	if (!medicine) {
		throw new Error('Medicine not found');
	}

	const warningTags = (warningRes?.data?.data || []).map((tag: { tagName: string; id: string }) => ({
		label: tag.tagName,
		value: tag.id,
	}));

	const categories = categoryRes.data.data || [];
	const categoryList = categories.map((item: { categoryName?: string; id: string }) => ({
		label: item?.categoryName,
		value: item?.id,
	}));
	const subCategoryList = categories
		.flatMap((item: { subCategories?: { subCategoryName?: string; id: string }[] }) => item?.subCategories || [])
		.map((subItem: { subCategoryName?: string; id: string }) => ({
			label: subItem?.subCategoryName,
			value: subItem?.id,
		}));

	const doseFormsData = doseRes.data.data || [];
	const doseForms = doseFormsData.map((item: { name?: string; id: string }) => ({
		label: item?.name,
		value: item?.id,
	}));

	let doseFormOptionsForCategory: { label: string; value: string }[] = [];
	if (medicine.categoryId) {
		const mapRes = await getApi(`/category-dose-form-mapping/get-by-category/${medicine.categoryId}`);
		if (mapRes?.data?.responseStatus !== 200) {
			throw new Error(mapRes?.data?.message || 'Failed to load dose form mapping');
		}
		doseFormOptionsForCategory = (mapRes?.data?.data || []).map((d: { id: string; name: string }) => ({
			label: d.name,
			value: d.id,
		}));
		const currentDoseFormId = medicine.doseFormId;
		if (
			currentDoseFormId &&
			!doseFormOptionsForCategory.some((o) => o.value === currentDoseFormId) &&
			doseForms.length
		) {
			const current = doseForms.find((df: { label: string; value: string }) => df.value === currentDoseFormId);
			if (current) doseFormOptionsForCategory.push(current);
		}
	}

	return {
		medicine,
		wholeCategories: categories,
		categoryList,
		subCategoryList,
		doseForms,
		doseFormOptionsForCategory,
		warningTags,
	};
}
